<template>
  <Renderer
    v-if="isEnabled"
    :value="value"
    @input="$ev => $emit('input', $ev)"
    :schema="schema"
    :config="config"
    :data-key="schema.key"
    v-on="$listeners"
  />
</template>

<script>
import { MediaComponent } from '@kvass/media'
import { FileComponent } from 'vue-elder-file'
import { CheckboxComponent } from 'vue-elder-checkbox'
import Renderer from './Renderer'
import Fields from './Fields'
import { path } from 'ramda'
import { RemoveBrackets, BracketsRegex, ValidateCondition } from './utils'

import { setup as MediaSetup } from '@kvass/media'
import { setup as FileSetup } from 'vue-elder-file'
import { VueComponentUpload as upload } from '@kvass/storage'
import { getMediaTypes } from '@kvass/media-types'

MediaSetup({ upload })
FileSetup({ upload })

function getComponent(component, value, validator, components = []) {
  if (component.includes(':'))
    return {
      component: Fields.Dynamic,
      binding: path(['options', 'props', '_parent'], value) || {},
      parentKey: value.key,
    }

  let defaultBinding = {
    ...(path(['options', 'props'], value) || {}),
  }

  if (typeof path(['options', 'validation'], value) === 'string')
    defaultBinding.required = Boolean(
      (path(['options', 'validation'], value) || '').match('required'),
    )

  let key = [value.parentKey, value.key].filter(Boolean).join('.')

  if (validator && key in validator.fields) {
    defaultBinding['isValid'] = validator.fields[key].valid
    defaultBinding['validationMessage'] = validator.fields[key].error
  }

  let customComponent = components.find(c => c.name === component)
  if (customComponent) return { component: customComponent, binding: { ...defaultBinding } }

  switch (component) {
    default:
      return { component: Fields.Input, binding: { ...defaultBinding } }
    case 'SelectModal':
    case 'Select':
      return { component: Fields.SelectModal, binding: { ...defaultBinding } }
    case 'SelectList':
      return { component: Fields.Select, binding: { ...defaultBinding } }
    case 'Date':
      return { component: Fields.Input, binding: { ...defaultBinding, type: 'date' } }
    case 'TextArea':
      return { component: Fields.TextArea, binding: { ...defaultBinding } }
    case 'Number':
      return {
        component: Fields.Number,
        binding: {
          ...defaultBinding,
          mask: {
            mask: Number,
            unmask: true,
            thousandsSeparator: ' ',
          },
        },
      }
    case 'Currency':
      return {
        component: Fields.Number,
        binding: {
          suffix: 'NOK',
          ...defaultBinding,
          align: 'right',
          mask: {
            mask: Number,
            unmask: true,
            thousandsSeparator: ' ',
          },
        },
      }
    case 'URL':
      return {
        component: Fields.Input,
        binding: {
          prefix: 'URL',
          ...defaultBinding,
        },
      }
    case 'MediaMultiple':
      return {
        component: MediaComponent,
        binding: {
          types: getMediaTypes(),
          uploadOptions: { compression: 'default', placement: 'inside', size: 'contain' },
          ...defaultBinding,
          multiple: true,
        },
      }
    case 'Media':
      return {
        component: MediaComponent,
        binding: {
          types: getMediaTypes(),
          uploadOptions: { compression: 'default', placement: 'inside', size: 'contain' },
          ...defaultBinding,
        },
      }
    case 'Image':
      return {
        component: MediaComponent,
        binding: {
          uploadOptions: { compression: 'default', placement: 'inside', size: 'contain' },
          ...defaultBinding,
        },
      }
    case 'Images':
      return {
        component: MediaComponent,
        binding: {
          uploadOptions: { compression: 'default' },
          ...defaultBinding,
          multiple: true,
        },
      }

    case 'ColorSelector':
      return {
        component: Fields.ColorSelector,
        binding: { ...defaultBinding },
      }
    case 'Instagram':
      return {
        component: Fields.Instagram,
        binding: { ...defaultBinding },
      }
    case 'FooterContent':
      return {
        component: Fields.FooterContent,
        binding: { ...defaultBinding },
      }
    case 'ResidentialTemplate':
      return {
        component: Fields.ResidentialTemplate,
        binding: { ...defaultBinding },
      }
    case 'File':
      return { component: FileComponent, binding: { ...defaultBinding } }
    case 'Files':
      return { component: FileComponent, binding: { ...defaultBinding, multiple: true } }
    case 'RichText':
      return { component: Fields.RichText, binding: { ...defaultBinding } }
    case 'Checkbox':
      return { component: CheckboxComponent, binding: { ...defaultBinding } }
    case 'Checklist':
      return { component: Fields.Checklist, binding: { ...defaultBinding } }
    case 'Radio':
      return { component: Fields.Radio, binding: { ...defaultBinding } }
    case 'Alert':
      return { component: Fields.Alert, binding: { ...defaultBinding } }
    case 'Html':
      return { component: Fields.Html, binding: { ...defaultBinding } }
    case 'Calc':
      return { component: Fields.Calc, binding: { ...defaultBinding } }
    case 'Tags':
      return { component: Fields.Tags, binding: { ...defaultBinding } }
  }
}

export default {
  props: {
    value: null,
    schema: Object,
  },
  inject: ['injection'],
  computed: {
    validator() {
      return this.injection.validator
    },
    components() {
      return this.injection.components
    },
    data() {
      return this.injection.data
    },
    isEnabled() {
      return ValidateCondition(
        path(['options', 'condition'], this.schema),
        this.schema.component,
        this.data,
      )
    },
    config() {
      let componentName = RemoveBrackets(this.schema.component)
      let config = getComponent(componentName, this.schema, this.validator, this.components)
      let isList = Boolean(this.schema.component.match(BracketsRegex))
      return {
        isList,
        removable: path(['options', 'removable'], this.schema) || false,
        showLabelCount: path(['options', 'showLabelCount'], this.schema) || false,
        componentName,
        ...config,
      }
    },
  },
  components: {
    Renderer,
  },
}
</script>
